// src/encounter/note-column/note-docs-feed.tsx
//
// The note's DOCS section reads the SAME encounter-scoped documents query the Docs STEP renders
// from (`DocumentsChartTab` -> `usePatientDocuments`), so react-query serves both from one cache
// entry and the step and the note can never list different documents.
//
// WHY THE FETCH LIVES HERE AND NOT IN `EncounterShellPage`
// --------------------------------------------------------
// Calling `usePatientDocuments` at page level added a real `useQuery` to a component that TWENTY
// page-level suites render with every service hook mocked and no `QueryClientProvider` — measured:
// all four `encounter-shell-page.preenc-gate.test.tsx` cases failed with "No QueryClient set".
// Those suites belong to other lanes; adding a mock line to each of them to accommodate this
// change would be the wrong direction of dependency.
//
// So the query is mounted only where the note actually renders, and only when the host has both
// ids. `NoteDocsFeed` CONDITIONALLY RENDERS the fetching component — it does not conditionally
// call a hook — which is why `encounter-drawer.test.tsx` and friends, which pass no ids, still
// mount no query at all.
import type { ReactNode } from 'react';
import { usePatientDocuments } from '@/patients/services/use-patient-documents';
import { formatDateTime, parseNoZIsoDatetime } from '@/shared/lib/date-format';
import type { GeneratedNoteDoc as NoteDocRow } from '@scripsteam/scrips-signal-ds';

/**
 * Flutter's encounter Docs fetch and the React Docs step both page at 20
 * (`defaultPatientDocumentPageSize`). The note MIRRORS the step, so it reads the same page rather
 * than issuing a second, deeper query.
 */
const NOTE_DOCS_PAGE_SIZE = 20;

export interface NoteDocsFeedProps {
  patientId?: string | null;
  encounterId?: string | null;
  /** Receives the rows, or `undefined` while there is nothing to read. */
  children: (docs: NoteDocRow[] | undefined) => ReactNode;
}

export function NoteDocsFeed({ patientId, encounterId, children }: NoteDocsFeedProps) {
  // Gated on the RESOLVED encounter id exactly as the Docs step is: an ungated fetch is
  // PATIENT-wide and would flash cross-encounter documents into the note (PROD-1238).
  if (!patientId || !encounterId) return <>{children(undefined)}</>;
  return (
    <NoteDocsQuery patientId={patientId} encounterId={encounterId}>
      {children}
    </NoteDocsQuery>
  );
}

function NoteDocsQuery({
  patientId,
  encounterId,
  children,
}: {
  patientId: string;
  encounterId: string;
  children: (docs: NoteDocRow[] | undefined) => ReactNode;
}) {
  const { data } = usePatientDocuments({
    patientId,
    encounterId,
    pageNumber: 1,
    pageSize: NOTE_DOCS_PAGE_SIZE,
  });
  return <>{children(data ? data.map(toNoteDocRow) : undefined)}</>;
}

/**
 * `DocumentRowWidget` (`docs_note_component_widget.dart:103-130`): the title, then
 * `"Updated by ${updatedByName ?? createdByName} on ${scDateTimeFormat(updatedOn ?? createdOn)}"`.
 *
 * A row missing its author or its date renders the TITLE ALONE rather than "Updated by  on " —
 * Flutter's interpolation would print the empty halves, and a clinical record should not carry a
 * sentence with holes in it.
 */
export function toNoteDocRow(
  d: {
    id?: string | null;
    name?: string | null;
    type?: string | null;
    updatedByName?: string | null;
    createdByName?: string | null;
    updatedOn?: string | null;
    createdOn?: string | null;
  },
  i: number,
): NoteDocRow {
  const who = d.updatedByName ?? d.createdByName ?? undefined;
  const whenRaw = d.updatedOn ?? d.createdOn ?? undefined;
  const when = whenRaw ? formatDateTime(parseNoZIsoDatetime(whenRaw)) : undefined;
  return {
    id: d.id ?? `doc-${i}`,
    title: d.name ?? '',
    updatedLine: who && when ? `Updated by ${who} on ${when}` : undefined,
    fileType: d.type ?? undefined,
  };
}
